import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TableContainer,
  Switch,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
} from '@mui/material';
import * as XLSX from 'xlsx';

// Yardımcı Fonksiyonlar (Değişmedi)
const isNumeric = (value) => !isNaN(parseFloat(value)) && isFinite(value);
const isDate = (value) => !isNaN(Date.parse(value));
const averageWordCount = (texts) => {
  const wordCounts = texts.map((text) =>
    typeof text === "string" ? text.split(/\s+/).length : 0
  );
  return wordCounts.length > 0
    ? wordCounts.reduce((sum, count) => sum + count, 0) / wordCounts.length
    : 0;
};

const guessColumnType = (columnName, columnData, columnIndex) => {
  const uniqueValues = new Set(columnData);
  const isFirstColumn = columnIndex === 0;
  const allUnique = uniqueValues.size === columnData.length;

  if (isFirstColumn && allUnique) return 'ID';

  const sampleValues = columnData.slice(0, 50);

  // Check if any of the sample values are numeric
  const hasNumeric = sampleValues.some((val) => isNumeric(val));
  if (hasNumeric) {
    return 'Data';
  }

  // If no numeric values, default to Verbatim
  return 'Verbatim';
};

const createCodebookPairs = (codeKeyData = [], codebookData = []) => {
  const maxLength = codebookData.length; // Sadece codebookData'nın uzunluğunu baz al
  const pairs = [];

  for (let i = 0; i < maxLength; i++) {
    const codeKeyValue = i < codeKeyData.length ? codeKeyData[i] : '';
    const codebookValue = codebookData[i] || '';

    pairs.push({
      codeKeyData: codeKeyValue,
      codebookData: codebookValue,
    });
  }
  return pairs;
};

export default function NewProjectPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [projectCode, setProjectCode] = useState('');
  const [codingLanguage, setCodingLanguage] = useState('English');
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [columns, setColumns] = useState([]);
  const [secondSheetColumns, setSecondSheetColumns] = useState([]);
  const [secondSheetData, setSecondSheetData] = useState([]);
  const [verbatimColumns, setVerbatimColumns] = useState([]);
  const [codebookSelections, setCodebookSelections] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Dosya Yükleme İşlevi
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onload = (event) => {
        const data = event.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const firstSheetName = workbook.SheetNames[0];
        const firstSheet = workbook.Sheets[firstSheetName];
        const firstSheetDataRaw = XLSX.utils.sheet_to_json(firstSheet, { defval: "" });
        // Boş satırları filtrele
        const firstSheetData = firstSheetDataRaw.filter(row => 
          Object.values(row).some(cell => cell !== "" && cell != null)
        );

        if (firstSheetData.length > 0) {
          const headers = Object.keys(firstSheetData[0]).filter((header) => {
            const trimmedHeader = header.trim();
            return trimmedHeader !== "" && !trimmedHeader.startsWith("__EMPTY");
          });
          const previewRows = firstSheetData.slice(0, 5);

          const guessedColumns = headers.map((header, index) => {
            const columnData = firstSheetData.map((row) => row[header]);
            const guessedType = guessColumnType(header, columnData, index);
            const dataCount = columnData.filter(cell => cell != null && cell !== '').length;
            return { 
              name: header, 
              type: guessedType, 
              columnNumber: `Column ${index + 1}`, 
              dataCount 
            };
          });

          setColumns(guessedColumns);
          setPreviewData(previewRows);

          const verbatimCols = guessedColumns.filter((col) => col.type === 'Verbatim');
          setVerbatimColumns(verbatimCols);

          if (workbook.SheetNames.length > 1) {
            const secondSheetName = workbook.SheetNames[1];
            const secondSheet = workbook.Sheets[secondSheetName];
            const secondSheetDataRaw = XLSX.utils.sheet_to_json(secondSheet, { defval: "" });
            const filteredSecondSheetData = secondSheetDataRaw.filter(row => 
              Object.values(row).some(cell => cell !== "" && cell != null)
            );
            setSecondSheetData(filteredSecondSheetData);
            if (filteredSecondSheetData.length > 0) {
              const secondHeaders = Object.keys(filteredSecondSheetData[0]).filter((header) => {
                const trimmedHeader_second = header.trim();
                return trimmedHeader_second !== "" && !trimmedHeader_second.startsWith("__EMPTY");
              });

              setSecondSheetColumns(secondHeaders);

              const initialCodebookSelections = {};
              verbatimCols.forEach((col) => {
                const codebookCol = secondHeaders.find((header) => header === col.name);
                const codeKeyCol = secondHeaders.find((header) =>
                  header.startsWith('code-' + col.name)
                );

                const codebookData = codebookCol ? filteredSecondSheetData.map((row) => row[codebookCol]) : [];
                const codeKeyData = codeKeyCol ? filteredSecondSheetData.map((row) => row[codeKeyCol]) : [];
                
                // Modified line: Only check for codebookCol existence
                const codebookPairs = codebookCol ? createCodebookPairs(codeKeyData, codebookData) : [];

                initialCodebookSelections[col.columnNumber] = {
                  addCodebook: !!codebookCol,
                  manualMaster: false,
                  masterFormat: 'code-master',
                  masterText: '',
                  codebookColumn: codebookCol || '',
                  selectedCodeKeyColumn: codeKeyCol || '',
                  codeKeyColumn: codebookPairs, // Now includes pairs even without key column
                };
              });
              setCodebookSelections(initialCodebookSelections);
            }
          } else {
            setSecondSheetColumns([]);
            setSecondSheetData([]);
            setCodebookSelections({});
          }

          setError('');
        } else {
          setError(t('emptySheet') || 'The uploaded sheet is empty');
          setPreviewData(null);
          setColumns([]);
          setVerbatimColumns([]);
          setCodebookSelections({});
          setSecondSheetData([]);
        }
      };
      reader.readAsBinaryString(selectedFile);
    }
  };

  // Sütun Türü Değiştirme
  const handleColumnTypeChange = (index, type) => {
    setColumns((prevColumns) => {
      const newColumns = [...prevColumns];
      newColumns[index].type = type;
      const updatedVerbatimCols = newColumns.filter((col) => col.type === 'Verbatim');
      setVerbatimColumns(updatedVerbatimCols);
      return newColumns;
    });
  };

  // Codebook Seçimlerini Güncelleme
  const handleCodebookChange = (columnNumber, field, value) => {
    setCodebookSelections((prev) => {
      const updatedSelections = {
        ...prev,
        [columnNumber]: {
          ...prev[columnNumber],
          [field]: value,
        },
      };

      if (!updatedSelections[columnNumber].manualMaster && field === 'codeKeyColumn') {
        updatedSelections[columnNumber].selectedCodeKeyColumn = value;
        const codebookCol = updatedSelections[columnNumber].codebookColumn;
        const codeKeyCol = value;
        if (codebookCol ) {
          const codebookData = secondSheetData.map((row) => row[codebookCol]);
          const codeKeyData = codeKeyCol ? secondSheetData.map((row) => row[codeKeyCol]) : [];
          updatedSelections[columnNumber].codeKeyColumn = createCodebookPairs(codeKeyData, codebookData);
        } else {
          updatedSelections[columnNumber].codeKeyColumn = [];
        }
      }

      return updatedSelections;
    });
  };

  // Manuel Master Değişikliklerini Yönetme
  const handleManualMasterChange = (columnNumber, field, value) => {
    setCodebookSelections((prev) => {
      const updatedSelections = {
        ...prev,
        [columnNumber]: {
          ...prev[columnNumber],
          [field]: value,
        },
      };

      if (field === 'manualMaster') {
        if (value) {
          updatedSelections[columnNumber].selectedCodeKeyColumn = '';
          updatedSelections[columnNumber].codeKeyColumn = [];
        } else {
          const codeKeyCol = updatedSelections[columnNumber].selectedCodeKeyColumn;
          if (codeKeyCol) {
            const codebookCol = updatedSelections[columnNumber].codebookColumn;
            if (codebookCol) {
              const codebookData = secondSheetData.map((row) => row[codebookCol]);
              const codeKeyData = codeKeyCol ? secondSheetData.map((row) => row[codeKeyCol]) : [];
              updatedSelections[columnNumber].codeKeyColumn = createCodebookPairs(codeKeyData, codebookData);
            }
          }
        }
      }

      if (field === 'masterText' && updatedSelections[columnNumber].manualMaster) {
        const lines = value.split('\n').filter((line) => line.trim() !== '');
        const pairedData = lines.map((line) => {
          const [code, response] = line.split('\t').map((item) => item.trim());
          return { codeKeyData: code || '', codebookData: response || '' };
        });
        updatedSelections[columnNumber].codeKeyColumn = pairedData;
      }

      return updatedSelections;
    });
  };

  // Form Gönderme
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!projectCode || !file) {
      setError(t('fillAllFields') || 'Please fill all fields');
      return;
    }

    const idColumns = columns.filter((col) => col.type === 'ID');
    const verbatimCols = columns.filter((col) => col.type === 'Verbatim');
    if (idColumns.length !== 1 || verbatimCols.length < 1) {
      setError(
        t('selectOneIDAndAtLeastOneVerbatim') ||
          'Please select exactly one ID column and at least one Verbatim column'
      );
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('projectCode', projectCode);
    formData.append('file', file);
    formData.append('useremail', currentUser.email);
    formData.append('codingLanguage', codingLanguage);
    formData.append('columns', JSON.stringify(columns));
    formData.append('codebookSelections', JSON.stringify(codebookSelections));

    try {
      const response = await fetch('https://codini-project-0-gt-795950092325.europe-west3.run.app', {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) throw new Error(t('createProjectFailed') || 'Project creation failed');
      navigate('/ProjectsPage');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: '#f4f4f4', minHeight: '100vh', padding: '20px' }}>
      <Container sx={{ mt: 1, backgroundColor: '#fff', borderRadius: 2, padding: '3px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt:2, mb: 2 }}>
          <Typography variant="h5" gutterBottom>
            {t('newProject')}
          </Typography>
          <Button variant="outlined" onClick={() => navigate('/ProjectsPage')}>
            {t('cancel')}
          </Button>
        </Box>
        <form onSubmit={handleSubmit}>
          <TextField
            label={t('projectCode')}
            value={projectCode}
            onChange={(e) => setProjectCode(e.target.value)}
            fullWidth
            margin="normal"
            required
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>{t('codingLanguage')}</InputLabel>
            <Select
              value={codingLanguage}
              onChange={(e) => setCodingLanguage(e.target.value)}
              label={t('codingLanguage')}
            >
              <MenuItem value="English">English</MenuItem>
              <MenuItem value="Turkish">Turkish</MenuItem>
              <MenuItem value="German">German</MenuItem>
              <MenuItem value="French">French</MenuItem>
              <MenuItem value="Spanish">Spanish</MenuItem>
              <MenuItem value="Italian">Italian</MenuItem>
              <MenuItem value="Dutch">Dutch</MenuItem>
              <MenuItem value="Greek">Greek</MenuItem>
              <MenuItem value="Portuguese">Portuguese</MenuItem>
              <MenuItem value="Hungarian">Hungarian</MenuItem>
              <MenuItem value="Swedish">Swedish</MenuItem>
              <MenuItem value="Norwegian">Norwegian</MenuItem>
              <MenuItem value="Finnish">Finnish</MenuItem>
              <MenuItem value="Arabic">Arabic</MenuItem>
              <MenuItem value="Indonesian">Indonesian</MenuItem>
              <MenuItem value="Azerbaijani">Azerbaijani</MenuItem>
              <MenuItem value="Slovene">Slovene</MenuItem>
              <MenuItem value="Danish">Danish</MenuItem>
              <MenuItem value="Romanian">Romanian</MenuItem>
              <MenuItem value="Russian">Russian</MenuItem>
              <MenuItem value="Estonian">Estonian</MenuItem>
              <MenuItem value="Latvian">Latvian</MenuItem>
              <MenuItem value="Lithuanian">Lithuanian</MenuItem>
              <MenuItem value="Thai">Thai</MenuItem>
              <MenuItem value="Polish">Polish</MenuItem>
              <MenuItem value="Slovak">Slovak</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ mt: 2 }}>
            <input
              type="file"
              accept=".xlsx"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              id="file-input"
            />
            <label htmlFor="file-input">
              <Button variant="contained" component="span" sx={{ background: '#fb5607' }}>
                {t('selectFile')}
              </Button>
            </label>
            {file && <Typography variant="body2" sx={{ mt: 1 }}>{file.name}</Typography>}
          </Box>

          {previewData && (
            <>
              <Typography variant="body1" sx={{ mt: 2 }}>
                {t('selectColumnTypes')}
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 400, overflow: 'auto' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      {columns.map((column, index) => (
                        <TableCell key={index}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Typography variant="body2">{`${column.columnNumber} - ${column.name}`}</Typography>
                            <Select
                              value={column.type}
                              onChange={(e) => handleColumnTypeChange(index, e.target.value)}
                              displayEmpty
                              size="small"
                              sx={{ mt: 1, width: '100%', backgroundColor: '#fdf6b2', fontSize: '0.8rem' }}
                            >
                              <MenuItem value="ID" sx={{ fontSize: '0.8rem' }}>ID</MenuItem>
                              <MenuItem value="Verbatim" sx={{ fontSize: '0.8rem' }}>Verbatim</MenuItem>
                              <MenuItem value="Data" sx={{ fontSize: '0.8rem' }}>Data</MenuItem>
                            </Select>
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.map((row, rowIndex) => (
                      <TableRow key={rowIndex}>
                        {columns.map((column, colIndex) => (
                          <TableCell key={colIndex} sx={{ fontSize: '0.8rem' }}>
                            {row[column.name]}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {verbatimColumns.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6">{t('verbatimColumns')}</Typography>
              {verbatimColumns.map((col) => (
                <Box key={col.columnNumber} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
                  <Box sx={{ background: '#bfdbf7', border: 'none', borderRadius: '4px', padding: '3px'}}>
                    <Typography variant="body2">{`${col.columnNumber} - ${col.name}`}</Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={codebookSelections[col.columnNumber]?.addCodebook || false}
                        onChange={(e) =>
                          handleCodebookChange(col.columnNumber, 'addCodebook', e.target.checked)
                        }
                      />
                    }
                    label={t('addCodebookFor')}
                  />
                  {codebookSelections[col.columnNumber]?.addCodebook && (
                    <>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={codebookSelections[col.columnNumber]?.manualMaster || false}
                            onChange={(e) =>
                              handleManualMasterChange(col.columnNumber, 'manualMaster', e.target.checked)
                            }
                          />
                        }
                        label={t('addManualMaster')}
                      />
                      {codebookSelections[col.columnNumber]?.manualMaster ? (
                        <>
                          <FormControl component="fieldset" sx={{ mt: 1 }}>
                            <FormLabel component="legend">{t('selectFormat')}</FormLabel>
                            <RadioGroup
                              row
                              value={codebookSelections[col.columnNumber]?.masterFormat || 'code-master'}
                              onChange={(e) =>
                                handleManualMasterChange(col.columnNumber, 'masterFormat', e.target.value)
                              }
                            >
                              <FormControlLabel value="code-master" control={<Radio />} label={t('codeMasterText') || "Code - Master Text"} />
                              <FormControlLabel value="master-only" control={<Radio />} label={t('masterOnly') || "Master Only"} />
                            </RadioGroup>
                          </FormControl>
                          <TextField
                            label={t('pasteMasterHere')}
                            multiline
                            rows={4}
                            fullWidth
                            sx={{ mt: 1 }}
                            value={codebookSelections[col.columnNumber]?.masterText || ''}
                            onChange={(e) =>
                              handleManualMasterChange(col.columnNumber, 'masterText', e.target.value)
                            }
                          />
                        </>
                      ) : (
                        <>
                          <FormControl fullWidth sx={{ mt: 1 }}>
                            <InputLabel>{t('selectCodebookColumn')}</InputLabel>
                            <Select
                              value={codebookSelections[col.columnNumber]?.codebookColumn || ''}
                              onChange={(e) =>
                                handleCodebookChange(col.columnNumber, 'codebookColumn', e.target.value)
                              }
                            >
                              {secondSheetColumns.map((colName) => (
                                <MenuItem key={colName} value={colName}>
                                  {colName}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <FormControl fullWidth sx={{ mt: 1 }}>
                            <InputLabel>{t('selectCodeKeyColumn')}</InputLabel>
                            <Select
                              value={codebookSelections[col.columnNumber]?.selectedCodeKeyColumn || ''}
                              onChange={(e) =>
                                handleCodebookChange(col.columnNumber, 'codeKeyColumn', e.target.value)
                              }
                            >
                              {secondSheetColumns.map((colName) => (
                                <MenuItem key={colName} value={colName}>
                                  {colName}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </>
                      )}
                    </>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
          <Box sx={{ mt: 2, mb: 2 }}>
            <Button type="submit" variant="contained" color="primary" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : t('createProject')}
            </Button>
          </Box>
        </form>
      </Container>
    </Box>
  );
}